import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreatmentsPage = () => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        const response = await axios.get(`${API}/treatments`);
        setTreatments(response.data.treatments);
      } catch {
        // silently handle fetch failure
      } finally {
        setLoading(false);
      }
    };
    fetchTreatments();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <div className="bg-clinic-bg" data-testid="treatments-page">
      {/* Hero Section with Video */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="treatments-hero">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/treatments-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-clinic-bg/30" />
        </div>

        <div className="container-clinic relative z-10">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
            className="max-w-3xl"
          >
            <motion.p variants={fadeInUp} className="text-caption mb-4">
              Treatments
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">
              Specialist surgical & non-surgical care
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-body-lg">
              Comprehensive expertise across plastic, reconstructive, and aesthetic surgery. 
              Each treatment is approached with precision, safety, and a commitment to 
              natural-looking results.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Treatments Grid */}
      <section className="pb-20 md:pb-32" data-testid="treatments-grid-section">
        <div className="container-clinic">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] bg-clinic-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {treatments.map((treatment, index) => (
                <motion.div
                  key={treatment.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <Link
                    to={`/treatments/${treatment.id}`}
                    className="group block bg-white border border-clinic-border hover:border-clinic-stone transition-colors duration-500 h-full"
                    data-testid={`treatment-link-${treatment.id}`}
                  >
                    <div className="aspect-[3/2] overflow-hidden">
                      <img
                        src={treatment.image}
                        alt={treatment.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        data-testid={`treatment-image-${treatment.id}`}
                      />
                    </div>
                    <div className="p-6 md:p-8">
                      <p className="text-caption mb-2">{treatment.subtitle}</p>
                      <h2 className="text-heading-3 text-clinic-charcoal mb-3 group-hover:text-clinic-stone transition-colors">
                        {treatment.title}
                      </h2>
                      <p className="text-body-lg text-sm line-clamp-2 mb-6">
                        {treatment.description}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-clinic-border">
                        <span className="text-xs font-body text-clinic-stone">
                          {treatment.procedures.length} procedures
                        </span>
                        <ArrowRight 
                          size={16} 
                          className="text-clinic-charcoal group-hover:translate-x-1 transition-transform" 
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding bg-clinic-muted" data-testid="treatments-info-section">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-heading-2 text-clinic-charcoal mb-6">
                A considered approach to every procedure
              </h2>
              <p className="text-body-lg mb-6">
                Whether you're considering surgery for aesthetic, reconstructive, or 
                medical reasons, the first step is always a thorough consultation. 
                We take time to understand your goals, discuss realistic outcomes, 
                and ensure you have all the information needed to make an informed decision.
              </p>
              <p className="text-body-lg">
                There is no pressure to proceed. Our role is to guide and support, 
                providing expert opinion and honest advice.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              {[
                {
                  title: 'Comprehensive Assessment',
                  text: 'Every treatment begins with a detailed consultation to assess your individual needs and suitability.'
                },
                {
                  title: 'Tailored Treatment Plans',
                  text: 'No two patients are the same. Treatment plans are customised to your anatomy, goals, and lifestyle.'
                },
                {
                  title: 'Ongoing Support',
                  text: 'From consultation through recovery, we provide continuous care and follow-up to ensure optimal outcomes.'
                }
              ].map((item, index) => (
                <div key={item.title} className="p-6 bg-white border border-clinic-border" data-testid={`info-card-${index}`}>
                  <h3 className="font-heading text-xl text-clinic-charcoal mb-2">{item.title}</h3>
                  <p className="text-body-lg text-sm">{item.text}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="treatments-cta-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Interested in a treatment?
            </h2>
            <p className="text-body-lg mb-10">
              Book a consultation to discuss your options with Mr Maheshwari in a 
              confidential, supportive environment.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="treatments-cta-btn"
            >
              Request a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentsPage;
